import { useState, useRef, useCallback, useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { CaretPosition } from "../../../types/ui";
import { Caret } from "../types/ui";

interface InfoTooltipProps {
  info: React.ReactNode;
  caret?: Caret;
  showIcon?: boolean;
  width?: string;
  label?: string;
}

const VIEWPORT_PADDING = 8; // min distance from viewport edges
const MIN_TOOLTIP_HEIGHT = 100; // flip below if less than this space above trigger

const InfoTooltip: React.FC<InfoTooltipProps> = ({
  info,
  caret = { position: CaretPosition.BOTTOM, color: "#1C9C6D" },
  showIcon = true,
  width = "214px",
  label = "More information",
}) => {
  const tooltipId = useId();
  const [visible, setVisible] = useState(false);
  const [flippedDown, setFlippedDown] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const triggerRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vvWidth = window.visualViewport?.width ?? window.innerWidth;
    const vvOffsetTop = window.visualViewport?.offsetTop ?? 0;
    const widthPx = parseInt(width, 10) || 214;
    const idealLeft = rect.left + rect.width / 2 - widthPx / 2;
    const clampedLeft = Math.min(
      Math.max(VIEWPORT_PADDING, idealLeft),
      vvWidth - widthPx - VIEWPORT_PADDING,
    );
    const spaceAbove = rect.top - vvOffsetTop;
    const showAbove = spaceAbove >= MIN_TOOLTIP_HEIGHT;
    setFlippedDown(!showAbove);
    setCoords({
      top: showAbove
        ? rect.top - VIEWPORT_PADDING
        : rect.bottom + VIEWPORT_PADDING,
      left: clampedLeft,
    });
    setVisible(true);
  }, [width]);

  useEffect(() => {
    if (!visible) return;
    const onScroll = () => setVisible(false);
    window.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [visible]);

  if (!info) return null;

  return (
    <div
      ref={triggerRef}
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-describedby={visible ? tooltipId : undefined}
      className={showIcon ? "inline-block font-medium" : "absolute inset-0"}
      onMouseEnter={show}
      onMouseLeave={() => setVisible(false)}
      onFocus={show}
      onBlur={() => setVisible(false)}
      onKeyDown={(event) => {
        if (event.key === "Escape") setVisible(false);
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          show();
        }
      }}
      onClick={show}
    >
      {showIcon && (
        <span>
          <Info className="w-4 h-4 text-lightgreen" />
        </span>
      )}
      {visible &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="et-analytics-tooltip fixed z-[9999] bg-white text-[#252525] shadow-[0_0_10px_0_rgba(0,0,0,0.1)] text-sm rounded-[10px] flex flex-col gap-[10px]"
            style={{
              width,
              padding: "10px 15px",
              top: coords.top,
              left: coords.left,
              transform: flippedDown ? "none" : "translateY(-100%)",
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setVisible(false);
              }}
              className="hidden [@media(hover:none)]:flex absolute top-1 right-1 w-5 h-5 items-center justify-center et-analytics-muted text-[#25252566] rounded"
              aria-label="Close"
            >
              ×
            </button>
            <div className="[@media(hover:none)]:pr-4">{info}</div>
            {caret &&
              (flippedDown ? (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-0 -translate-y-full w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent"
                  style={{ borderBottomColor: caret.color }}
                />
              ) : (
                <div
                  className="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent"
                  style={{ borderTopColor: caret.color }}
                />
              ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default InfoTooltip;
